import {
  CHARGE_TYPE_LABELS,
  CHARGE_TYPES,
  INVOICE_STATUSES,
  storageDetailLines,
  storageDetailNotes,
  type BillingCharge,
  type ChargeType,
  type RateCard,
  type ServiceInvoice,
  type ServiceTariff,
  type StorageDetail,
} from '@wms/domain';
import { Fragment, useState } from 'react';
import { api } from '../../api';
import { csvDecimal, downloadCsv } from '../../csv';
import { fmt } from '../../format';
import { navigate } from '../../router';
import { useSession } from '../../session';
import { Empty, ErrorBanner, Spinner, useLoad } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';
import { TariffTable } from '../../ui/tariff';

type Tab = 'charges' | 'invoices' | 'rates';
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const CARRIERS: Record<string, string> = { yurtici: 'Yurtiçi Kargo', aras: 'Aras Kargo', mng: 'MNG Kargo', ptt: 'PTT Kargo', surat: 'Sürat Kargo' };

/** The warehouse's service charges to the seller and its invoices: route #/faturalar and #/faturalar/:invoiceId */
export function SellerBilling({ invoiceId }: { invoiceId: string | null }) {
  const [tab, setTab] = useState<Tab>('charges');
  if (invoiceId) return <InvoiceView id={invoiceId} />;
  return (
    <>
      <div className="tabs" role="tablist">
        {(
          [
            ['charges', 'Ücret dökümü'],
            ['invoices', 'Hizmet faturaları'],
            ['rates', 'Barem ve tarifeler'],
          ] as const
        ).map(([key, label]) => (
          <button key={key} role="tab" aria-selected={tab === key} className={tab === key ? 'on' : ''} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'charges' && <Charges />}
      {tab === 'invoices' && <Invoices />}
      {tab === 'rates' && <Rates />}
    </>
  );
}

const PERIODS = [
  { key: 'this', label: 'Bu ay' },
  { key: 'last', label: 'Geçen ay' },
  { key: '90', label: 'Son 90 gün' },
] as const;

function period(key: (typeof PERIODS)[number]['key']): { from: string; to: string } {
  const now = new Date();
  const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  if (key === 'this') return { from: isoDay(monthStart), to: isoDay(now) };
  if (key === 'last') return { from: isoDay(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))), to: isoDay(new Date(monthStart.getTime() - 86_400_000)) };
  return { from: isoDay(new Date(now.getTime() - 90 * 86_400_000)), to: isoDay(now) };
}

/**
 * How a storage line was worked out (0067): measured desi, unmeasured goods by their share of the bin
 * they are in or by the desi per unit, the seller's share of each pallet bin.
 */
function StorageHow({ detail }: { detail: StorageDetail }) {
  const lines = storageDetailLines(detail);
  // the rules of the seller's own tariff: by the bin (₺ / m³ / ay) or by the desi per unit (₺ / desi / gün)
  const notes = storageDetailNotes(detail);
  return (
    <div className="stack" style={{ gap: 4, fontSize: 13 }}>
      <b>Nasıl hesaplandı</b>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      {notes.length > 0 && <span className="muted">{notes.join(' ')}</span>}
    </div>
  );
}

const hasHow = (c: BillingCharge) => c.detail !== null && storageDetailLines(c.detail).length > 0;

/** Every billable service, line by line: depolama, mal kabul, elleçleme, kargo (with its desi). */
function Charges() {
  const [periodKey, setPeriodKey] = useState<(typeof PERIODS)[number]['key']>('this');
  const [type, setType] = useState<ChargeType | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const { from, to } = period(periodKey);
  const charges = useLoad(() => api<BillingCharge[]>('GET', `/api/billing/charges?from=${from}&to=${to}`), [from, to]);
  const all = charges.data ?? [];
  const rows = type ? all.filter((c) => c.type === type) : all;
  const sum = (list: readonly BillingCharge[]) => list.reduce((n, c) => n + Number(c.amount), 0);
  const shownTypes = CHARGE_TYPES.filter((t) => all.some((c) => c.type === t));
  // amounts come as decimal texts: compared as numbers
  const sort = useSort<BillingCharge>('charges', {
    date: { label: 'Tarih', value: (c) => c.serviceDate, desc: true },
    type: { label: 'Hizmet', value: (c) => CHARGE_TYPES.indexOf(c.type) },
    description: { label: 'Açıklama', value: (c) => c.description },
    desi: { label: 'Desi', value: (c) => (c.desi === null ? null : Number(c.desi)), desc: true },
    amount: { label: 'Tutar', value: (c) => Number(c.amount), desc: true },
    // the ones still waiting for an invoice first
    invoiced: { label: 'Fatura', value: (c) => c.invoiceId !== null },
  });

  return (
    <>
      <div className="row">
        {PERIODS.map((p) => (
          <button key={p.key} className={`chip select${periodKey === p.key ? ' on' : ''}`} onClick={() => setPeriodKey(p.key)}>
            {p.label}
          </button>
        ))}
        <span className="muted">
          {fmt.date(from)} – {fmt.date(to)}
        </span>
        <span style={{ flex: 1 }} />
        <button
          className="btn sm"
          disabled={rows.length === 0}
          onClick={() =>
            downloadCsv(`hak-edis-${from}-${to}.csv`, [
              ['Tarih', 'Hizmet', 'Açıklama', 'Desi', 'Miktar', 'Birim fiyat', 'Tutar (TL, KDV hariç)', 'Faturalandı'],
              ...rows.map((c) => [c.serviceDate, CHARGE_TYPE_LABELS[c.type], c.description, csvDecimal(c.desi), csvDecimal(c.quantity), csvDecimal(c.unitPrice), csvDecimal(c.amount), c.invoiceId ? 'Evet' : 'Hayır']),
            ])
          }
        >
          <Icon name="download" size={16} /> CSV
        </button>
      </div>

      <div className="grid">
        <button className="card kpi" style={{ textAlign: 'left', cursor: 'pointer', outline: type === null ? '2px solid var(--brand)' : 'none' }} onClick={() => setType(null)}>
          <span className="n">{fmt.money(sum(all))}</span>
          <span className="l">Toplam (KDV hariç)</span>
          <span className="h">{fmt.n(all.length)} hizmet kalemi</span>
        </button>
        {shownTypes.map((t) => (
          <button key={t} className="card kpi" style={{ textAlign: 'left', cursor: 'pointer', outline: type === t ? '2px solid var(--brand)' : 'none' }} onClick={() => setType(type === t ? null : t)}>
            <span className="n">{fmt.money(sum(all.filter((c) => c.type === t)))}</span>
            <span className="l">{CHARGE_TYPE_LABELS[t]}</span>
            <span className="h">{fmt.n(all.filter((c) => c.type === t).length)} kalem</span>
          </button>
        ))}
      </div>

      <section className="card">
        {charges.error && <ErrorBanner message={charges.error} onRetry={() => void charges.reload()} />}
        {!charges.data ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty title="Bu dönemde hizmet kalemi yok" />
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  {sort.th('date')}
                  {sort.th('type')}
                  {sort.th('description')}
                  {sort.th('desi', 'num')}
                  {sort.th('amount', 'num')}
                  {sort.th('invoiced')}
                </tr>
              </thead>
              <tbody>
                {sort.apply(rows).map((c) => {
                  const how = hasHow(c);
                  const expanded = how && open === c.id;
                  return (
                    <Fragment key={c.id}>
                      <tr className={how ? 'clickable' : undefined} onClick={how ? () => setOpen(expanded ? null : c.id) : undefined} aria-expanded={how ? expanded : undefined}>
                        <td className="nowrap">{fmt.date(c.serviceDate)}</td>
                        <td>{CHARGE_TYPE_LABELS[c.type]}</td>
                        <td>
                          {c.description}
                          {how && (
                            <div>
                              <span className="btn sm ghost" style={{ marginTop: 4 }}>
                                <Icon name={expanded ? 'x' : 'list'} size={16} /> {expanded ? 'Kapat' : 'Nasıl hesaplandı?'}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="num">{c.desi === null ? '—' : fmt.dec(Number(c.desi))}</td>
                        <td className="num">{fmt.money(c.amount)}</td>
                        <td>{c.invoiceId ? <span className="chip ok">Faturalandı</span> : <span className="chip">Bekliyor</span>}</td>
                      </tr>
                      {expanded && (
                        <tr className="sub-row">
                          <td colSpan={6}>
                            <StorageHow detail={c.detail!} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

const INVOICE_STATUS: Record<ServiceInvoice['status'], { label: string; tone: string }> = {
  draft: { label: 'Taslak', tone: '' },
  issued: { label: 'Kesildi', tone: 'info' },
  paid: { label: 'Ödendi', tone: 'ok' },
  cancelled: { label: 'İptal', tone: 'bad' },
};

function Invoices() {
  const invoices = useLoad(() => api<ServiceInvoice[]>('GET', '/api/billing/invoices'), []);
  const sort = useSort<ServiceInvoice>('invoices', {
    number: { label: 'Fatura no', value: (i) => i.invoiceNumber },
    period: { label: 'Dönem', value: (i) => i.periodStart, desc: true },
    subtotal: { label: 'Ara toplam', value: (i) => Number(i.subtotal), desc: true },
    vat: { label: 'KDV', value: (i) => Number(i.vatAmount), desc: true },
    total: { label: 'Genel toplam', value: (i) => Number(i.total), desc: true },
    status: { label: 'Durum', value: (i) => INVOICE_STATUSES.indexOf(i.status) },
  });
  return (
    <section className="card">
      {invoices.error && <ErrorBanner message={invoices.error} onRetry={() => void invoices.reload()} />}
      {!invoices.data ? (
        <Spinner />
      ) : invoices.data.length === 0 ? (
        <Empty title="Henüz hizmet faturası yok">Faturalar her ayın başında önceki ayın hizmet ücretlerinden kesilir.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                {sort.th('number')}
                {sort.th('period')}
                {sort.th('subtotal', 'num')}
                {sort.th('vat', 'num')}
                {sort.th('total', 'num')}
                {sort.th('status')}
                <th />
              </tr>
            </thead>
            <tbody>
              {sort.apply(invoices.data).map((i) => (
                <tr key={i.id} className="clickable" onClick={() => navigate(`/faturalar/${i.id}`)}>
                  <td className="mono">{i.invoiceNumber}</td>
                  <td>{fmt.month(i.periodStart)}</td>
                  <td className="num">{fmt.money(i.subtotal)}</td>
                  <td className="num">
                    {fmt.money(i.vatAmount)} <span className="muted">(%{fmt.dec(Number(i.vatRate))})</span>
                  </td>
                  <td className="num">
                    <b>{fmt.money(i.total)}</b>
                  </td>
                  <td>
                    <span className={`chip ${INVOICE_STATUS[i.status].tone}`}>{INVOICE_STATUS[i.status].label}</span>
                  </td>
                  <td className="num">
                    <span className="btn sm">
                      <Icon name="eye" size={16} /> Görüntüle
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * The barem (desi range → price) and fixed fees that apply to the seller: its own where it has them,
 * else the list price (the website's prices), worded as the website does (per month, by the step).
 */
function Rates() {
  const cards = useLoad(() => api<RateCard[]>('GET', '/api/billing/rate-cards'), []);
  const tariffs = useLoad(() => api<ServiceTariff[]>('GET', '/api/billing/tariffs'), []);
  if (cards.error) return <ErrorBanner message={cards.error} onRetry={() => void cards.reload()} />;
  if (!cards.data || !tariffs.data) return <Spinner />;
  // a seller's own card replaces the list price for the same service and carrier
  const applies = (c: RateCard) => c.tenantId !== null || !cards.data!.some((o) => o.tenantId !== null && o.kind === c.kind && o.carrierCode === c.carrierCode);
  const tariff = tariffs.data.find((t) => t.tenantId !== null) ?? tariffs.data[0] ?? null;

  return (
    <div className="split">
      <div className="stack">
        {cards.data.length === 0 && (
          <section className="card">
            <Empty title="Desi baremi yok">Toplama ve paketleme sabit ücretlerle ücretlenir; kargo bu tarifede yok (ayrıca faturalanır).</Empty>
          </section>
        )}
        {[...cards.data]
          .sort((a, b) => Number(applies(b)) - Number(applies(a)) || a.kind.localeCompare(b.kind))
          .map((c) => (
            <section key={c.id} className="card" style={{ opacity: applies(c) ? 1 : 0.6 }}>
              <div className="card-h">
                <h2>{c.kind === 'shipping' ? `Kargo barem${c.carrierCode ? ` · ${CARRIERS[c.carrierCode] ?? c.carrierCode}` : ''}` : 'Elleçleme (toplama & paketleme) barem'}</h2>
                {c.tenantId ? <span className="chip ok">Size özel</span> : <span className="chip">Liste fiyatı</span>}
                {!applies(c) && <span className="chip">Size özel tarife geçerli</span>}
                <span className="spacer" />
                <span className="sub">
                  {fmt.date(c.validFrom)} – {c.validTo ? fmt.date(c.validTo) : 'süresiz'}
                </span>
              </div>
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th>Koli başına desi</th>
                      <th className="num">Fiyat (KDV hariç)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.tiers.map((tier) => (
                      <tr key={tier.minDesi}>
                        <td>
                          {fmt.dec(tier.minDesi)} – {fmt.dec(tier.maxDesi)} desi
                        </td>
                        <td className="num">{fmt.money(tier.unitPrice)}</td>
                      </tr>
                    ))}
                    {c.extraDesiPrice && (
                      <tr>
                        <td>{fmt.dec(c.tiers.at(-1)?.maxDesi ?? 0)} desi üzeri her desi</td>
                        <td className="num">+ {fmt.money(c.extraDesiPrice)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
      </div>
      <section className="card">
        <div className="card-h">
          <h2>Sabit hizmet ücretleri</h2>
          {tariff && (tariff.tenantId ? <span className="chip ok">Size özel</span> : <span className="chip">Liste fiyatı</span>)}
        </div>
        {!tariff ? (
          <Empty title="Tanımlı tarife yok">Sabit hizmet ücreti alınmıyor.</Empty>
        ) : (
          <div className="table-wrap">
            <TariffTable tariff={tariff} />
          </div>
        )}
        {cards.data.length > 0 && (
          <div className="card-b muted" style={{ fontSize: 13 }}>
            Kargo ve elleçleme, koli başına faturalanabilir desiye göre (yukarı yuvarlanır, en az 1 desi) baremden hesaplanır.
          </div>
        )}
      </section>
    </div>
  );
}

/** One Depolama & Elleçleme Hizmet Faturası, laid out for printing (Yazdır → PDF olarak kaydet). */
function InvoiceView({ id }: { id: string }) {
  const { me } = useSession();
  const invoice = useLoad(() => api<ServiceInvoice>('GET', `/api/billing/invoices/${id}`), [id]);
  const charges = useLoad(() => api<BillingCharge[]>('GET', `/api/billing/charges?invoiceId=${id}`), [id]);
  if (invoice.error) return <ErrorBanner message={invoice.error} onRetry={() => void invoice.reload()} />;
  if (!invoice.data) return <Spinner />;
  const inv = invoice.data;

  return (
    <>
      <div className="row no-print">
        <a className="btn ghost" href="#/faturalar">
          <Icon name="back" /> Faturalar
        </a>
        <span style={{ flex: 1 }} />
        <button
          className="btn"
          disabled={!charges.data?.length}
          onClick={() =>
            downloadCsv(`${inv.invoiceNumber}-dokum.csv`, [
              ['Tarih', 'Hizmet', 'Açıklama', 'Desi', 'Miktar', 'Tutar (TL, KDV hariç)'],
              ...(charges.data ?? []).map((c) => [c.serviceDate, CHARGE_TYPE_LABELS[c.type], c.description, csvDecimal(c.desi), csvDecimal(c.quantity), csvDecimal(c.amount)]),
            ])
          }
        >
          <Icon name="download" /> Döküm (CSV)
        </button>
        <button className="btn primary" onClick={() => window.print()}>
          <Icon name="printer" /> Yazdır / PDF
        </button>
      </div>

      <section className="card invoice">
        <div className="card-b stack">
          <div className="head">
            <div>
              <div className="muted" style={{ fontWeight: 600 }}>
                Depolama & Elleçleme Hizmet Faturası
              </div>
              <h1 className="mono">{inv.invoiceNumber}</h1>
              <div className="muted">
                Dönem: {fmt.date(inv.periodStart)} – {fmt.date(inv.periodEnd)}
                {inv.issuedAt && ` · Kesim: ${fmt.date(inv.issuedAt)}`}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700 }}>{me.tenant?.name}</div>
              <div className="muted">Satıcı kodu {me.tenant?.code}</div>
            </div>
          </div>
          <table className="t">
            <thead>
              <tr>
                <th>Hizmet</th>
                <th className="num">Miktar</th>
                <th className="num">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l) => (
                <tr key={l.lineNo}>
                  <td>{l.description}</td>
                  <td className="num">{fmt.dec(Number(l.quantity))}</td>
                  <td className="num">{fmt.money(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="totals">
            <div>
              <span>Ara toplam</span>
              <span>{fmt.money(inv.subtotal)}</span>
            </div>
            <div>
              <span>KDV (%{fmt.dec(Number(inv.vatRate))})</span>
              <span>{fmt.money(inv.vatAmount)}</span>
            </div>
            <div className="grand">
              <span>Genel toplam</span>
              <span>{fmt.money(inv.total)}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Hizmet dökümü</h2>
          <span className="sub">{charges.data ? `${fmt.n(charges.data.length)} kalem` : ''}</span>
        </div>
        {!charges.data ? (
          <Spinner />
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Açıklama</th>
                  <th className="num">Desi</th>
                  <th className="num">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {charges.data.map((c) => (
                  <tr key={c.id}>
                    <td className="nowrap">{fmt.date(c.serviceDate)}</td>
                    <td>{c.description}</td>
                    <td className="num">{c.desi === null ? '—' : fmt.dec(Number(c.desi))}</td>
                    <td className="num">{fmt.money(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
